// @flow

import { useEffect, useState } from 'react';
import * as AuthAPI from '../../../../services/auth-api';
import * as RoleModel from '../../../../models/role';

const EMPTY: RoleModel.RolesCatalogueT = {
  status: 'error',
  roles: [],
  defaultRoleIds: [],
};

// Required (default) roles first, then alphabetically by display label
// within each group. Sorting happens once here so every consumer of the
// catalogue renders the same order.
const sortCatalogue = (
  roles: RoleModel.catalogueRoleT[],
): RoleModel.catalogueRoleT[] =>
  [...roles].sort((a, b) => {
    if (!!a.defaultRole !== !!b.defaultRole) return a.defaultRole ? -1 : 1;
    return RoleModel.roleLabel(a).localeCompare(
      RoleModel.roleLabel(b),
      undefined,
      { sensitivity: 'base' },
    );
  });

// Fetches the authorisation service's role catalogue for the lifetime of the
// page that mounts this hook. Deliberately outside Redux: the catalogue is
// scoped to /settings/users and refetched on every visit.
export const useRolesCatalogue = (): RoleModel.RolesCatalogueT => {
  const [catalogue, setCatalogue] = useState<RoleModel.RolesCatalogueT>({
    ...EMPTY,
    status: 'loading',
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await AuthAPI.getAllRoles();
        const data = (response && response.data) || [];
        const roles = sortCatalogue(Array.isArray(data) ? data : []);
        if (!mounted) return;
        setCatalogue({
          status: 'ready',
          roles,
          defaultRoleIds: roles.filter((r) => r.defaultRole).map((r) => r.id),
        });
      } catch (e) {
        if (mounted) setCatalogue({ ...EMPTY, status: 'error' });
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return catalogue;
};
